import { Switch } from '@headlessui/react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useField } from 'remix-validated-form'
import { FormInputWrapperProps } from './FormInputWrapper'
import { debug } from 'lib/logging'

type FormInputProps = {
  readOnly?: boolean
  onToggle?: (val: boolean) => void
  errorClassName?: string
  offLabel?: string
  onLabel?: string
  defaultValue?: boolean
} & Omit<FormInputWrapperProps<HTMLInputElement>, `defaultValue`>

export default function FormToggle({
  offLabel,
  defaultValue,
  onLabel,
  name,
  readOnly,
  errorClassName,
  onToggle,
}: FormInputProps) {
  const { error } = useField(name)
  const [isOn, setIsOn] = useState<boolean>(defaultValue ?? false)

  useEffect(() => {
    if (onToggle) {
      onToggle(isOn)
    }
  }, [isOn])

  useEffect(() => {
    debug(`toggle`, `defaultValue`, defaultValue)
    if (!!defaultValue !== isOn) setIsOn(!!defaultValue)
  }, [defaultValue])

  return (
    <>
      <div className="flex items-center justify-start">
        <Switch
          name={name}
          checked={isOn}
          onChange={setIsOn}
          className={`${
            isOn ? `bg-blue-600` : `bg-surface-strong`
          } relative inline-flex h-7 w-12 items-center rounded-full disabled:cursor-not-allowed disabled:bg-surface-strong`}
          disabled={readOnly}
        >
          <span
            className={classNames(
              isOn ? `translate-x-6` : `translate-x-1`,
              `flex h-5 w-5 transform items-center justify-center rounded-full bg-surface transition`,
              readOnly && `opacity-50`,
            )}
          >
            {isOn ? (
              <CheckIcon className="h-4 w-4 fill-current stroke-current stroke-[0.7px] text-blue-600" />
            ) : (
              <XMarkIcon className="h-4 w-4 fill-current stroke-current stroke-[0.7px] text-(--surface-strong)" />
            )}
          </span>
        </Switch>
        <p className="ml-3 text-ui-sm text-strong">{isOn ? onLabel : offLabel}</p>
      </div>
      {error && <div className={classNames(errorClassName, `mt-0.5 text-sm text-red-600`)}>{error}</div>}
    </>
  )
}
